import { useState, useRef, useEffect } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/TranslationContext";
import { hasPermission } from "@/lib/permissions";
import { toast } from "sonner";
import { VoicePicker } from "@/components/VoicePicker";
import { getVoice, saveVoice } from "@/lib/voices";
import { narrateUrl } from "@/lib/narrate";
import { startNarration, endNarration } from "@/lib/narrator";
import { SpeakingIndicator } from "@/components/SpeakingIndicator";
import { TaggedBlocks } from "@/components/TaggedBlocks";
import { Sparkles, X, Send, ShieldCheck, Mail, FileDown, Check, Mic, Square, Volume2, VolumeX, Loader2, Repeat } from "lucide-react";

const NARRATE_KEY = "erio_ask_autonarrate";
const CONVO_KEY = "erio_ask_convomode";

// Tiny markdown: **bold**, bullet/numbered lines, paragraphs.
const renderRich = (text) =>
  text.split("\n").map((line, i) => {
    const bolded = line.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
      seg.startsWith("**") && seg.endsWith("**") ? (
        <strong key={j}>{seg.slice(2, -2)}</strong>
      ) : (
        <span key={j}>{seg}</span>
      )
    );
    const trimmed = line.trim();
    const isBullet = /^[*-]\s/.test(trimmed) || /^\d+[.)]\s/.test(trimmed);
    if (!trimmed) return <div key={i} className="h-2" />;
    return (
      <p key={i} className={isBullet ? "pl-3 -indent-2" : ""}>
        {bolded}
      </p>
    );
  });

export const AskErio = () => {
  const { user } = useAuth();
  const { tx, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [emailFor, setEmailFor] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [savedIdx, setSavedIdx] = useState(new Set());
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [autoNarrate, setAutoNarrate] = useState(
    () => localStorage.getItem(NARRATE_KEY) !== "0"
  );
  const [convoMode, setConvoMode] = useState(
    () => localStorage.getItem(CONVO_KEY) === "1"
  );
  const [speakingIdx, setSpeakingIdx] = useState(null); // message index currently loading/playing
  const [voice, setVoice] = useState(getVoice);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioRef = useRef(null);
  const speakCache = useRef({}); // index -> data url
  const convoRef = useRef(convoMode);
  convoRef.current = convoMode;

  const toggleAutoNarrate = () => {
    setAutoNarrate((v) => {
      const next = !v;
      localStorage.setItem(NARRATE_KEY, next ? "1" : "0");
      if (!next) stopAudio();
      return next;
    });
  };

  const toggleConvoMode = () => {
    setConvoMode((v) => {
      const next = !v;
      localStorage.setItem(CONVO_KEY, next ? "1" : "0");
      if (next) { localStorage.setItem(NARRATE_KEY, "1"); setAutoNarrate(true); }
      return next;
    });
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      endNarration(audioRef.current);
      audioRef.current = null;
    }
    setSpeakingIdx(null);
  };

  const speak = async (idx, text) => {
    // Toggle off if this message is already playing.
    if (speakingIdx === idx && audioRef.current) {
      stopAudio();
      return;
    }
    stopAudio();
    setSpeakingIdx(idx);
    try {
      const cacheKey = `${idx}:${voice}`;
      let src = speakCache.current[cacheKey];
      if (!src) {
        src = await narrateUrl(text, voice);
        speakCache.current[cacheKey] = src;
      }
      const audio = new Audio(src);
      audioRef.current = audio;
      startNarration(audio, () => { audioRef.current = null; setSpeakingIdx(null); });
      audio.onended = () => {
        endNarration(audio);
        audioRef.current = null;
        setSpeakingIdx(null);
        // Conversation mode: hands-free — reopen the mic for a follow-up.
        if (convoRef.current) setTimeout(() => maybeListenAgain(), 500);
      };
      await audio.play();
    } catch (err) {
      setSpeakingIdx(null);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Narration unavailable.");
    }
  };

  useEffect(() => {
    if (open && suggestions.length === 0) {
      api.get("/copilot/suggestions").then(({ data }) => setSuggestions(data.suggestions)).catch(() => {});
    }
  }, [open, suggestions.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  if (!user || !hasPermission(user, "dashboard.read")) return null;

  const send = async (q, viaVoice = false) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const { data } = await api.post("/copilot/ask", {
        question,
        session_id: sessionRef.current,
        locale,
      });
      sessionRef.current = data.session_id;
      let newIdx = -1;
      setMessages((m) => {
        newIdx = m.length;
        return [...m, {
          role: "assistant", text: data.answer,
          tagged_blocks: Array.isArray(data.tagged_blocks) ? data.tagged_blocks : [],
          grounded: data.grounded_in, question,
        }];
      });
      // Speak the answer back when dictated by voice, always-narrate, or convo mode.
      if ((viaVoice || autoNarrate || convoRef.current) && newIdx >= 0) {
        speak(newIdx, data.answer);
      }
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: formatApiErrorDetail(err.response?.data?.detail) || "Sorry, I couldn't answer that.", error: true, question },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const sendVoice = async (blob) => {
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, "question.webm");
      const { data } = await api.post("/copilot/transcribe", form);
      if (data.transcript) {
        send(data.transcript, true); // auto-narrate the answer
      } else {
        toast.error("Couldn't hear that — please try again.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Voice input failed.");
    } finally {
      setTranscribing(false);
    }
  };

  const maybeListenAgain = () => {
    // Hands-free follow-up: only if still in convo mode and idle.
    if (convoRef.current && !recording && !busy && !transcribing) {
      toggleRecording();
    }
  };

  const stopRecording = () => {
    recorderRef.current?.state === "recording" && recorderRef.current.stop();
    setRecording(false);
  };

  const toggleRecording = async () => {
    if (recording) { stopRecording(); return; }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Microphone not supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (blob.size > 0) sendVoice(blob);
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const saveToPack = async (msg, idx) => {
    setActionBusy(true);
    try {
      await api.post("/copilot/export", { question: msg.question, answer: msg.text });
      setSavedIdx((s) => new Set(s).add(idx));
      toast.success("Saved to the board pack's Executive Q&A appendix.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setActionBusy(false);
    }
  };

  const emailAnswer = async (msg) => {
    if (!emailTo.trim()) return;
    setActionBusy(true);
    try {
      await api.post("/copilot/email", { recipient: emailTo.trim(), question: msg.question, answer: msg.text });
      toast.success(`Emailed to ${emailTo.trim()}.`);
      setEmailFor(null);
      setEmailTo("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Email failed.");
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="ask-erio-fab"
          aria-label="Open Ask ERIO assistant"
          className="fixed bottom-6 right-6 z-[80] flex items-center gap-2 rounded-full bg-ek-ink px-5 py-3 text-sm font-semibold text-white shadow-xl transition-transform hover:scale-105"
        >
          <Sparkles size={17} className="text-ek-purple" />
          {tx("Ask ERIO")}
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-[80] flex h-[34rem] w-[26rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-black/10 bg-white shadow-2xl"
          data-testid="ask-erio-panel"
        >
          <div className="flex items-center justify-between bg-ek-ink px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <Sparkles size={17} className="text-ek-purple" />
              <span className="font-display text-sm font-semibold">{tx("Ask ERIO")}</span>
              <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-white/60">
                {tx("Copilot")}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <VoicePicker
                value={voice}
                onChange={(v) => { setVoice(v); saveVoice(v); stopAudio(); }}
                testid="ask-erio-voice-picker"
                dark
              />
              <button
                onClick={toggleConvoMode}
                data-testid="ask-erio-convo-toggle"
                title={convoMode ? "Conversation mode on — mic reopens after each spoken answer" : "Conversation mode off"}
                className={`transition-colors ${convoMode ? "text-ek-purple" : "text-white/60 hover:text-white"}`}
              >
                <Repeat size={16} />
              </button>
              <button
                onClick={toggleAutoNarrate}
                data-testid="ask-erio-autonarrate-toggle"
                title={autoNarrate ? "Auto-narrate is on — every answer is spoken" : "Auto-narrate is off"}
                className={`transition-colors ${autoNarrate ? "text-ek-purple" : "text-white/60 hover:text-white"}`}
              >
                {autoNarrate ? <Volume2 size={17} /> : <VolumeX size={17} />}
              </button>
              <button onClick={() => setOpen(false)} data-testid="ask-erio-close" className="text-white/60 hover:text-white">
                <X size={18} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="ask-erio-messages">
            {messages.length === 0 && (
              <div>
                <p className="text-sm text-ek-ink/65">
                  {tx("I'm grounded in your live, access-scoped risk data — ask me anything about your posture, risks, KRIs, suppliers or governance.")}
                </p>
                <div className="mt-4 space-y-2">
                  {suggestions.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`ask-erio-suggestion-${i}`}
                      className="block w-full rounded-md border border-black/10 px-3 py-2 text-left text-sm text-ek-ink/75 transition-colors hover:border-ek-purple hover:text-ek-purple"
                    >
                      {tx(s)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                <div
                  className={`max-w-[88%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-ek-purple text-white"
                      : m.error
                        ? "bg-red-50 text-red-700"
                        : "bg-ek-canvas text-ek-ink"
                  }`}
                  data-testid={`ask-erio-msg-${m.role}`}
                >
                  {m.role === "assistant" && !m.error && Array.isArray(m.tagged_blocks) && m.tagged_blocks.some((b) => b?.class && b.class !== "narrative") ? (
                    <TaggedBlocks
                      blocks={m.tagged_blocks}
                      fallbackText={m.text}
                      dense
                      testIdPrefix={`ask-erio-msg-${i}`}
                    />
                  ) : m.role === "assistant" ? renderRich(m.text) : m.text}
                  {m.role === "assistant" && m.error && m.question && (
                    <button
                      onClick={() => send(m.question)}
                      disabled={busy}
                      data-testid="ask-erio-retry"
                      className="mt-2 inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:opacity-40"
                    >
                      <Repeat size={11} /> {tx("Retry")}
                    </button>
                  )}
                  {m.grounded && (
                    <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-black/5 pt-2">
                      <ShieldCheck size={11} className="text-emerald-600" />
                      {m.grounded.map((g) => (
                        <span key={g} className="rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700">
                          {tx(g)}
                        </span>
                      ))}
                    </div>
                  )}
                  {m.role === "assistant" && !m.error && m.question && (
                    <div className="mt-2 border-t border-black/5 pt-2">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => speak(i, m.text)}
                          data-testid={`ask-erio-listen-${i}`}
                          className={`flex items-center gap-1 text-[11px] font-semibold transition-colors hover:text-ek-purple ${speakingIdx === i ? "text-ek-purple" : "text-ek-ink/65"}`}
                        >
                          {speakingIdx === i ? <Square size={11} /> : <Volume2 size={12} />}
                          {speakingIdx === i ? tx("Stop") : tx("Listen")}
                          {speakingIdx === i && <SpeakingIndicator className="ml-1 h-3 text-ek-purple" />}
                        </button>
                        <button
                          onClick={() => { setEmailFor(emailFor === i ? null : i); setEmailTo(""); }}
                          data-testid={`ask-erio-email-toggle-${i}`}
                          className="flex items-center gap-1 text-[11px] font-semibold text-ek-ink/65 transition-colors hover:text-ek-purple"
                        >
                          <Mail size={12} /> {tx("Email to board")}
                        </button>
                        <button
                          onClick={() => saveToPack(m, i)}
                          disabled={actionBusy || savedIdx.has(i)}
                          data-testid={`ask-erio-save-pack-${i}`}
                          className="flex items-center gap-1 text-[11px] font-semibold text-ek-ink/65 transition-colors hover:text-ek-purple disabled:text-emerald-600"
                        >
                          {savedIdx.has(i) ? <><Check size={12} /> {tx("Saved to pack")}</> : <><FileDown size={12} /> {tx("Save to pack")}</>}
                        </button>
                      </div>
                      {emailFor === i && (
                        <form
                          onSubmit={(e) => { e.preventDefault(); emailAnswer(m); }}
                          className="mt-2 flex items-center gap-1.5"
                        >
                          <input
                            type="email"
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            placeholder={tx("board@company.com")}
                            data-testid={`ask-erio-email-input-${i}`}
                            className="flex-1 rounded-md border border-black/15 px-2.5 py-1.5 text-xs focus:border-ek-purple focus:outline-none"
                          />
                          <button
                            type="submit"
                            disabled={actionBusy || !emailTo.trim()}
                            data-testid={`ask-erio-email-send-${i}`}
                            className="rounded-md bg-ek-ink px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                          >
                            {tx("Send")}
                          </button>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex items-center gap-1.5 text-sm text-ek-ink/65" data-testid="ask-erio-thinking">
                <span className="h-2 w-2 animate-bounce rounded-full bg-ek-purple [animation-delay:-0.2s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-ek-purple [animation-delay:-0.1s]" />
                <span className="h-2 w-2 animate-bounce rounded-full bg-ek-purple" />
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="flex items-center gap-2 border-t border-black/10 p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={transcribing ? tx("Transcribing…") : recording ? tx("Listening… tap to stop") : tx("Ask about your risks…")}
              data-testid="ask-erio-input"
              className="flex-1 rounded-full border border-black/15 px-4 py-2 text-sm focus:border-ek-purple focus:outline-none"
            />
            <button
              type="button"
              onClick={toggleRecording}
              disabled={busy || transcribing}
              data-testid="ask-erio-mic"
              title={recording ? tx("Stop recording") : tx("Ask by voice")}
              className={`flex h-9 w-9 items-center justify-center rounded-full transition-colors disabled:opacity-40 ${
                recording ? "bg-red-500 text-white animate-pulse" : "border border-black/15 text-ek-ink/60 hover:border-ek-purple hover:text-ek-purple"
              }`}
            >
              {transcribing ? <Loader2 size={15} className="animate-spin" /> : recording ? <Square size={14} /> : <Mic size={15} />}
            </button>
            <button
              type="submit"
              disabled={busy || !input.trim()}
              data-testid="ask-erio-send"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-ek-ink text-white disabled:opacity-40"
            >
              <Send size={15} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
